import React from 'react';
import { Plus, X } from 'lucide-react';
import type { Specialty } from '@/lib/api/specialties';
import { SpecialtySearch } from '@/components/forms/SpecialtySearch';

export interface AdditionalSpecialtiesInputProps {
  /** Specialties added so far: the code of a catalogue pick, or free text as typed */
  value: string[];
  onChange: (value: string[]) => void;
  /** Catalogue offered as suggestions while typing */
  specialties: Specialty[];
  disabled?: boolean;
  statusMessage?: string;
}

/**
 * Lets a practitioner add any number of specialties beyond their catalogue one.
 * Typing suggests catalogue specialties. A picked suggestion is kept as its
 * catalogue code; anything else is kept as typed (free text).
 */
export function AdditionalSpecialtiesInput({
  value,
  onChange,
  specialties,
  disabled,
  statusMessage,
}: AdditionalSpecialtiesInputProps) {
  const [draft, setDraft] = React.useState('');
  const labelByCode = React.useMemo(
    () => new Map(specialties.map((specialty) => [specialty.code, specialty.display])),
    [specialties]
  );

  const add = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const isRepeat = value.some((existing) => existing.toLowerCase() === trimmed.toLowerCase());
    if (!isRepeat) onChange([...value, trimmed]);
    setDraft('');
  };

  return (
    <div>
      <div className="flex gap-2">
        <div className="flex-1">
          <SpecialtySearch
            specialties={specialties}
            query={draft}
            onQueryChange={setDraft}
            onPick={(specialty) => add(specialty.code)}
            onEnterText={add}
            placeholder="Type a specialty and press Enter"
            disabled={disabled}
            statusMessage={statusMessage}
          />
        </div>
        <button
          type="button"
          onClick={() => add(draft)}
          disabled={disabled || !draft.trim()}
          className="flex h-9 shrink-0 items-center gap-1 rounded-md px-3 text-sm font-medium text-[#061a61] ring-1 ring-inset ring-gray-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>
      {value.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {value.map((specialty) => (
            <li
              key={specialty}
              className="flex items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-2.5 pr-1 text-xs text-gray-700"
            >
              {labelByCode.get(specialty) ?? specialty}
              <button
                type="button"
                onClick={() => onChange(value.filter((existing) => existing !== specialty))}
                disabled={disabled}
                aria-label={`Remove ${labelByCode.get(specialty) ?? specialty}`}
                className="rounded-full p-0.5 hover:bg-gray-200"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
